import { format } from 'date-fns';
import { toZonedTime } from 'date-fns-tz';
import {
  ScheduleAttainmentItem,
  ScheduleAttainmentPayload,
  ScheduleOverridePayload
} from 'Interfaces/JobsInterface';
import { action, computed, makeAutoObservable, observable, toJS } from 'mobx';
import { toast } from 'react-toastify';
import { ScheduleAttainmentServices } from 'services/ScheduleAttainmentServices';
import { handleApiError } from 'utils/handleApiError';

export interface DefaultShiftTimings {
  [key: string]: {
    start: string;
    end: string;
  };
}

// Define interface for form data entries
interface RoomShiftEntry {
  isRoomRunning: 'Y' | 'N';
  startTimeOverride: string;
  endTimeOverride: string;
}

// Define complete form data structure
interface FormDataStructure {
  [key: string]: {
    [key: string]: RoomShiftEntry;
  };
}

export class ScheduleAttainmentStore {
  scheduleData: ScheduleAttainmentItem[] = [];
  isLoading: boolean = false;
  date: Date = new Date();
  isSaving: boolean = false;
  count: number = 0;

  // Store the form data directly in the store
  formData: FormDataStructure = {};

  constructor() {
    makeAutoObservable(this, {
      formData: observable,
      isPastView: computed
    });
  }

  get isPastView(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Set to start of day for comparison

    const selectedDate = new Date(this.date);
    selectedDate.setHours(0, 0, 0, 0); // Set to start of day for comparison

    return selectedDate < today;
  }

  setDate = (date: Date) => {
    this.date = date;
    this.formData = {};
    this.fetchScheduleAttainment();
  };

  // Initialize form data with default values
  initializeFormData = (
    roomCodes: string[],
    shifts: string[],
    defaultShiftMap: DefaultShiftTimings
  ) => {
    const newFormData: FormDataStructure = {};

    roomCodes.forEach(roomCode => {
      newFormData[roomCode] = {};

      shifts.forEach(shift => {
        newFormData[roomCode][shift] = {
          isRoomRunning: 'Y',
          startTimeOverride: defaultShiftMap[shift].start,
          endTimeOverride: defaultShiftMap[shift].end
        };
      });
    });

    this.formData = newFormData;
  };

  /**
   * Formats a date or time string to HH:MM AM/PM format
   * @param {string} input - Date string in ISO format (e.g. "2025-05-04T14:15:00Z")
   *                        or time string in 24-hour format (e.g. "22:00:00")
   * @param {string} timeZone - Target timezone (optional, defaults to 'UTC')
   * @returns {string} Formatted time string in HH:MM AM/PM format
   */
  formatTimeForDisplay(input: string | null, timeZone = 'UTC') {
    if (input === null) {
      return '';
    }

    let date;

    // Check if input is a time-only string (e.g. "22:00:00")
    if (/^\d{1,2}:\d{2}(:\d{2})?$/.test(input)) {
      // Create a date object for today with the given time
      const [hours, minutes, seconds = '00'] = input.split(':');
      const today = new Date();
      date = new Date(
        today.getFullYear(),
        today.getMonth(),
        today.getDate(),
        parseInt(hours, 10),
        parseInt(minutes, 10),
        parseInt(seconds, 10)
      );
    } else {
      // Assume it's a full ISO date string
      date = new Date(input);
    }

    // Convert to the target timezone if provided
    const zonedDate = toZonedTime(date, timeZone);

    // Format the date to HH:MM AM/PM format
    return format(zonedDate, 'h:mm a');
  }

  // Update form data with API data
  updateFormDataFromAPI = () => {
    if (!this.scheduleData || this.scheduleData.length === 0) {
      return;
    }

    // Create a copy of existing form data to modify
    const updatedFormData = { ...toJS(this.formData) };

    this.scheduleData.forEach(item => {
      const roomCode = item.room.room_short;
      const shiftName = item.shift.shift_name;

      // Ensure the room and shift exist in the form data
      if (!updatedFormData[roomCode]) {
        updatedFormData[roomCode] = {};
      }

      if (!updatedFormData[roomCode][shiftName]) {
        updatedFormData[roomCode][shiftName] = {
          isRoomRunning: 'Y',
          startTimeOverride: '',
          endTimeOverride: ''
        };
      }

      // Update room running status
      updatedFormData[roomCode][shiftName].isRoomRunning = item.is_room_running ? 'Y' : 'N';

      // Update time overrides
      if (item.start_time_override) {
        updatedFormData[roomCode][shiftName].startTimeOverride = this.formatTimeForDisplay(
          item.start_time_override
        );
      } else {
        updatedFormData[roomCode][shiftName].startTimeOverride = this.formatTimeForDisplay(
          item.shift.start_time
        );
      }

      if (item.end_time_override) {
        updatedFormData[roomCode][shiftName].endTimeOverride = this.formatTimeForDisplay(
          item.end_time_override
        );
      } else {
        updatedFormData[roomCode][shiftName].endTimeOverride = this.formatTimeForDisplay(
          item.shift.end_time
        );
      }
    });

    // Update the store's form data
    this.formData = updatedFormData;
  };

  // Update a specific field in the form data
  updateFormField = action(
    (
      roomCode: string,
      shift: string,
      field: keyof RoomShiftEntry,
      value: string,
      defaultShiftMap: DefaultShiftTimings
    ) => {
      // Ensure the structure exists
      if (!this.formData[roomCode]) {
        this.formData[roomCode] = {};
      }

      if (!this.formData[roomCode][shift]) {
        this.formData[roomCode][shift] = {
          isRoomRunning: 'Y',
          startTimeOverride: defaultShiftMap[shift].start,
          endTimeOverride: defaultShiftMap[shift].end
        };
      }

      // Update the field
      this.formData[roomCode][shift][field] = value as any;
    }
  );

  fetchScheduleAttainment = async () => {
    this.isLoading = true;
    try {
      // Format date as DD-MM-YYYY using date-fns
      const formattedDate = format(this.date, 'yyyy-MM-dd');
      const payload: ScheduleAttainmentPayload = {
        date: formattedDate
      };
      const response = await ScheduleAttainmentServices.fetchScheduleAttainment(payload);
      this.scheduleData = response.results;
      this.count = response.count;

      // Update the form data with the new API data
      this.updateFormDataFromAPI();
    } catch (error) {
      toast.error('Error fetching schedule attainment data');
    } finally {
      this.isLoading = false;
    }
  };

  saveScheduleOverrides = async () => {
    this.isSaving = true;
    try {
      // Build the override payload directly from our form data
      const overrides = this.buildOverridePayloadFromStore();

      // Call API
      await ScheduleAttainmentServices.saveScheduleAttainment(overrides);
      toast.success('Schedule overrides saved successfully');

      // Refresh data
      this.fetchScheduleAttainment();
    } catch (error) {
      handleApiError(error, 'Error saving schedule overrides');
    } finally {
      this.isSaving = false;
    }
  };

  // Build the payload directly from the store's form data
  buildOverridePayloadFromStore = (): ScheduleOverridePayload[] => {
    const overrides: ScheduleOverridePayload[] = [];

    // Format date as YYYY-MM-DD for API
    const formattedDate = format(this.date, 'yyyy-MM-dd');

    // Process each room and shift
    Object.entries(this.formData).forEach(([roomCode, shifts]) => {
      Object.entries(shifts).forEach(([shiftName, data]) => {
        const isRoomRunning = data.isRoomRunning === 'Y';

        const override: ScheduleOverridePayload = {
          room_short: roomCode,
          shift_name: shiftName.toLowerCase(),
          override_date: formattedDate,
          is_room_running: isRoomRunning
        };

        // Add time overrides if room is running
        if (isRoomRunning) {
          if (data.startTimeOverride) {
            try {
              // 1. Get the date value from this.date as a string like yyyy-mm-dd
              const dateString = format(this.date, 'yyyy-MM-dd');

              // 2. Format the time from the startTimeOverride (e.g. "2:00 PM")
              // Parse the time string to extract hours and minutes in 24-hour format
              // const timeParts = data.startTimeOverride.match(/(\d+):(\d+)\s+(AM|PM)/i);
              // Using a more specific regex to prevent backtracking vulnerability
              const timeParts = data.startTimeOverride.match(/^(\d{1,2}):(\d{2})\s+(AM|PM)$/i);

              if (timeParts) {
                let hours = parseInt(timeParts[1], 10);
                const minutes = parseInt(timeParts[2], 10);
                const period = timeParts[3].toUpperCase();

                // Convert to 24-hour format
                if (period === 'PM' && hours < 12) {
                  hours += 12;
                } else if (period === 'AM' && hours === 12) {
                  hours = 0;
                }

                // 3. Combine date and time
                const timeString = `${hours.toString().padStart(2, '0')}:${minutes
                  .toString()
                  .padStart(2, '0')}:00`;
                const isoString = `${dateString}T${timeString}Z`;

                override.start_time_override = isoString;
              } else {
                override.start_time_override = data.startTimeOverride;
              }
            } catch (error) {
              override.start_time_override = data.startTimeOverride;
            }
          }

          if (data.endTimeOverride) {
            try {
              // 1. Get the date value from this.date as a string like yyyy-mm-dd
              const dateString = format(this.date, 'yyyy-MM-dd');

              // 2. Format the time from the endTimeOverride (e.g. "2:00 PM")
              // Parse the time string to extract hours and minutes in 24-hour format
              // Using a more specific regex to prevent backtracking vulnerability
              const timeParts = data.endTimeOverride.match(/^(\d{1,2}):(\d{2})\s+(AM|PM)$/i);

              if (timeParts) {
                let hours = parseInt(timeParts[1], 10);
                const minutes = parseInt(timeParts[2], 10);
                const period = timeParts[3].toUpperCase();

                // Convert to 24-hour format
                if (period === 'PM' && hours < 12) {
                  hours += 12;
                } else if (period === 'AM' && hours === 12) {
                  hours = 0;
                }

                // 3. Combine date and time
                const timeString = `${hours.toString().padStart(2, '0')}:${minutes
                  .toString()
                  .padStart(2, '0')}:00`;
                const isoString = `${dateString}T${timeString}Z`;

                override.end_time_override = isoString;
              } else {
                override.end_time_override = data.endTimeOverride;
              }
            } catch (error) {
              override.end_time_override = data.endTimeOverride;
            }
          }
        }

        overrides.push(override);
      });
    });

    return overrides;
  };
  // Legacy method kept for backward compatibility
  buildOverridePayload = (formData: any): ScheduleOverridePayload[] => {
    const overrides: ScheduleOverridePayload[] = [];
    // Format date as DD-MM-YYYY using date-fns
    const formattedDate = format(this.date, 'dd-MM-yyyy');
    // Process rooms data from form
    Object.keys(formData).forEach(key => {
      if (key.includes('_is_room_running')) {
        const roomShort = key.split('_')[0];
        const shiftName = key.split('_')[1];
        const isRoomRunning = formData[key] === 'Y';
        const override: ScheduleOverridePayload = {
          room_short: roomShort,
          shift_name: shiftName.toLowerCase(),
          override_date: formattedDate,
          is_room_running: isRoomRunning
        };
        // Add time overrides if room is running
        if (isRoomRunning) {
          const startTimeKey = `${roomShort}_${shiftName}_start_override`;
          const endTimeKey = `${roomShort}_${shiftName}_end_override`;
          if (formData[startTimeKey]) {
            override.start_time_override = formData[startTimeKey];
          }
          if (formData[endTimeKey]) {
            override.end_time_override = formData[endTimeKey];
          }
        }
        overrides.push(override);
      }
    });
    return overrides;
  };
}
